import { useEffect, useState, useCallback } from "react";
import axiosInstance from "../utils/axiosConfig";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardMedia from "@material-ui/core/CardMedia";
import Paper from "@material-ui/core/Paper";
import Button from "../Input/Button";
import LifeTopicPost from "./LifeTopicPost";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";


const useStyles = makeStyles({
    root: {
        minWidth: 300,
        minHeight: 100,
        marginTop: "50px",
        marginLeft: "1.75rem",
    },
    input: {
        height: "100%",
        resize: "none",
        overflow: "scroll",
    },
    title: {
        fontSize: 32,
        fontFamily: "GoodUnicorn",
        color: props => props.theme.primaryColor,
        zIndex: 1,
    },
    homeGrid: {
        "&::before": {
            content: "' '",
            background: props => `url(${props.theme.backgroundLogo}) no-repeat center center fixed`,
            opacity: ".6",
            backgroundSize: "cover",
            position: "absolute",
            top: "160px",
            right: "0px",
            bottom: "0px",
            left: "0px",
            zIndex: -1,
        },
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
    }
});

export default function Home({ user, isParent, isMentor, theme, showNotification }) {
    const classes = useStyles({ theme });
    // State to hold the posts for the latest life topic
    const [lifeTopicPosts, setLifeTopicPosts] = useState([]);
    // State to hold the latest life topic
    const [latestLifeTopic, setLatestLifeTopic] = useState(null);
    // State to track loading status
    const [isLoading, setIsLoading] = useState(true);
    
    // Function to fetch life topics and posts
    const fetchLifeTopicData = useCallback(async () => {
        if (!user) return;
        
        setIsLoading(true);
        try {
            // Fetch life topics - Queries the 'life_topics' table and returns the most recent row. 
            // life_topics table columns include: name, url, image_1, image_2, created_at, updated_at
            const allLifetopicsResponse = await axiosInstance.get(`/life_topics`);
            
            if (allLifetopicsResponse.data.length === 0) {
                console.log("No life topics found.");
                setIsLoading(false);
                return;
            }
            
            // Target the most recent life topic
            const latestLifeTopic = allLifetopicsResponse.data[allLifetopicsResponse.data.length - 1];
            setLatestLifeTopic(latestLifeTopic);
            
            // Fetch life topic posts - Queries the 'life_topic_posts' table and returns all rows for the most recent life topic. 
            // life_topic_posts table columns include: life_topic_id, user_upload_id, message, flagged, approved, user_id, hidden, created_at, updated_at
            const postsResponse = await axiosInstance.get(`/life_topic_posts/${latestLifeTopic.id}`);
            
            const sortedPosts = postsResponse.data.sort((a, b) =>  new Date(b.created_at) - new Date(a.created_at));
            
            setLifeTopicPosts(sortedPosts);
        } catch (error) {
            console.error("Error fetching life topic data:", error);
            showNotification("Failed to load life topic data", "error");
        } finally {
            setIsLoading(false);
        }
    }, [user, showNotification]);
    
    // Fetch life topic data when the component mounts or when user changes
    useEffect(() => {
        if (user) {
            fetchLifeTopicData();
        }
    }, [user, fetchLifeTopicData]);
    
    // Function to upload a life topic image
    const uploadLifeTopicImage = async (file, imageNumber) => {
        let formData = new FormData();
        formData.append("file", file);
        
        showNotification("Uploading...", "warning");
        try {
            await axiosInstance.post(
                `/life_topic/upload_${imageNumber}/${latestLifeTopic?.id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );
            showNotification("Post successful", "success");
            fetchLifeTopicData(); // Refresh data after upload
        } catch (error) {
            showNotification("Failed to upload.", "error");
        }
    };
    
    // Function to create a new post
    const makePost = async () => {
        let content = document.getElementById("contained-button-file").files[0];
        
        if (!content) {
            showNotification("Please attach a file before uploading!", "warning");
            return;
        }
        
        let status = document.getElementById("status").value;
        
        let formData = new FormData();
        formData.append("file", content);
        formData.append("status", status);
        
        showNotification("Uploading...", "warning");
        try {
            const result = await axiosInstance.post(
                `/life_topic_posts/${latestLifeTopic?.id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    }
                }
            );
            showNotification("Post created successfully", "success");
            setLifeTopicPosts(prevPosts => [result.data, ...prevPosts]);
        } catch (error) {
            showNotification(error.response?.data?.message || "Failed to create post", "error");
        }
    };
    
    // Function to remove a post
    const removePost = (postId) => {
        // Update state to remove the post with the given ID
        setLifeTopicPosts(currentPosts => currentPosts.filter(post => post.id !== postId));
    };
    
    // Show loading indicator while data is being fetched
    if (!user || isLoading) {
        return (
            <div className={classes.loadingContainer}>
                <CircularProgress />
                <Typography>Loading..</Typography>
            </div>
        );
    }
    
    return (
        <div style={{ marginTop: "1rem" }}>
            <Grid container spacing={2} className={classes.homeGrid}>
                {/* Left image */}
                    <Grid item xs={4} style={{ zIndex: 1, margin: "auto", paddingTop: "55px", marginTop: "50px" }}>
                        {
                            latestLifeTopic?.image_1?.length && latestLifeTopic?.image_1?.length > 0 ? (
                                <CardMedia
                                    style={{
                                        width: "95%",
                                        objectFit: "fill",
                                        margin: "auto",
                                        maxHeight: "320px",
                                        borderRadius: "10px",
                                    }}
                                    component="img"
                                    image={latestLifeTopic.image_1}
                                    title="Life Topic Image 1"
                                />
                                ) : 
                                <CardMedia
                                    style={{
                                        width: "95%",
                                        objectFit: "fill",
                                        margin: "auto",
                                        maxHeight: "320px",
                                        borderRadius: "10px",
                                    }}
                                    component="img"
                                    image="/images/lifetopic_fallback_1.png" /* If image_1 is undefined then always fallback to these production table image filenames*/
                                    title="Life Topic Image 1"
                            />
                        }
                        
                        {/* TODO: Should mentor be able to overwrite a life topic image was last posted? */}
                        {isMentor && (
                            <Card style={{ textAlign: "center", margin: "auto", width: "95%" }}>
                                <input
                                    accept="image/*"
                                    id="life-topic-first-image-upload"
                                    onChange={(event) => uploadLifeTopicImage(event.target.files[0], 1)}
                                    type="file"
                                    style={{ display: "none" }}
                                />
                                <label htmlFor="life-topic-first-image-upload">
                                    <Button style={{ fontWeight: "bold" }} theme={theme}>
                                        Attach
                                    </Button>
                                </label>
                            </Card>
                        )}
                </Grid>
                
                {/* Center content */}
                <Grid className={classes.title} item xs={4}>
                    <Typography className={classes.title} style={{ textAlign: "center" }}>
                        Life Topic
                        <br />
                        {latestLifeTopic?.created_at && new Date(latestLifeTopic.created_at).toLocaleDateString()}
                    </Typography>
                    {latestLifeTopic?.url && (
                        <iframe
                            type="text/html"
                            src={`${latestLifeTopic.url}?rel=0`}
                            width="100%"
                            height="76.75%"
                            style={{ borderRadius: "10px" }}
                            allowFullScreen
                            title="Life Topic Video"
                        />
                    )}
                </Grid>
                
                {/* Right image */}
                <Grid item xs={4} style={{ zIndex: 1, margin: "auto", paddingTop: "55px", marginTop: "50px" }}>
                    {
                        latestLifeTopic?.image_2 && latestLifeTopic?.image_2?.length > 0 ? (
                            <CardMedia
                                style={{
                                    width: "95%",
                                    objectFit: "fill",
                                    margin: "auto",
                                    maxHeight: "320px",
                                    borderRadius: "10px",
                                }}
                                component="img"
                                image={latestLifeTopic.image_2}
                                title="Life Topic Image 2"
                            />
                        ) : <CardMedia
                                style={{
                                    width: "95%",
                                    objectFit: "fill",
                                    margin: "auto",
                                    maxHeight: "320px",
                                    borderRadius: "10px",
                                }}
                                component="img"
                                image="/images/lifetopics_fallback_2.png" /* If image_2 is undefined then always fallback to these production table image filenames*/
                                title="Life Topic Image 2"
                        />
                    }
                    
                    {isMentor && (
                        <Card style={{ textAlign: "center", margin: "auto", width: "95%" }}>
                            <input
                                accept="image/*"
                                className={classes.input}
                                onChange={(event) => uploadLifeTopicImage(event.target.files[0], 2)}
                                type="file"
                                id="life-topic-second-image-upload"
                                style={{ display: "none" }}
                            />
                            <label htmlFor="life-topic-second-image-upload">
                                <Button style={{ fontWeight: "bold" }} theme={theme}>
                                    Attach
                                </Button>
                            </label>
                        </Card>
                    )}
                </Grid>
                
                <Grid item xs={4} />
                
                {/* Upload creation form for non-parent users */}
                {user?.isKid || !isParent && !isMentor && (
                    <Grid container item justifyContent="center" xs={4} spacing={3} style={{ zIndex: 1 }}>
                        <Card className={classes.root} style={{ width: "100%" }}>
                            <Grid container>
                                <Grid item xs={2}>
                                    <Typography style={{ margin: "15px", fontWeight: "bold" }}>
                                        Post
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Paper elevation={5} style={{ width: "100%", textAlign: "center", margin: "10px" }}>
                                        <TextField
                                            style={{ width: "100%", height: "30px" }}
                                            id="status"
                                            variant="filled"
                                            InputLabelProps={{
                                                style: { height: "30px" },
                                                shrink: "true"
                                            }}
                                            InputProps={{
                                                style: {
                                                    height: "35px",
                                                    backgroundColor: "white",
                                                    padding: 0,
                                                },
                                                disableUnderline: true
                                            }}
                                            inputProps={{
                                                style: { paddingTop: "10px", paddingBottom: "10px" },
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={1}>
                                    <IconButton
                                        style={{
                                            marginTop: "1rem",
                                            marginRight: "1rem",
                                            maxHeight: "32px",
                                            maxWidth: "32px",
                                            marginLeft: "4.25rem"
                                        }}
                                        onClick={makePost}
                                    >
                                        <img
                                            style={{
                                                maxHeight: "24px",
                                                maxWidth: "24px",
                                                marginBottom: "5px"
                                            }}
                                            src="/icons/001-paper-plane.png"
                                            alt="Send"
                                        />
                                    </IconButton>
                                </Grid>
                                <Grid item xs={3} />
                                <Grid item xs={2} />
                                <Grid item xs={4}>
                                    <input
                                        accept="image/*"
                                        className={classes.input}
                                        id="contained-button-file"
                                        type="file"
                                        style={{ display: "none" }}
                                    />
                                    <label htmlFor="contained-button-file">
                                        <Button style={{ fontWeight: "bold" }} theme={theme}>
                                            Attach
                                        </Button>
                                    </label>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                )}
                <Grid item xs={4} />
            </Grid>
            
            {/* Display life topic posts */}
            <Grid container spacing={2}>
                <Grid item xs={4} />
                    {lifeTopicPosts.map((post) => (
                        <LifeTopicPost
                            key={post.id}
                            showNotification={showNotification}
                            removePost={removePost}
                            theme={theme}
                            post={post}
                            user={user}
                            isParent={isParent}
                            isMentor={isMentor}
                        />
                    ))}
            </Grid>
        </div>
    );
}